import { useSearchParams } from '@remix-run/react';
import { useEffect, useState } from 'react';
import type {
    DropdownItem,
    EnquiryProfessional,
} from '@modules/enquiry/component/enquiry-form/EnquiryForm';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';
import EnquiryModal from '~/modules/enquiry/component/enquiry-modal/EnquiryModal';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    ENQUIRE_MODAL_SHOWUP_KEY,
    ENQUIRE_MODAL_TYPO_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY_VALUE as KEY_VALUE,
} from '~/modules/enquiry/constants';

interface RenderEnquiryModalProps {
    professional: EnquiryProfessional;
    branchOptions?: DropdownItem<Branch>[];
    onOpenChange?: (open: boolean) => void;
}

export const useEnquiryModalByUrl = (
    alwaysRenderModal = false,
    showModalByDefault = false
) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { mobile } = useAppDisplayModeContext();
    const [isOpen, setIsOpen] = useState(showModalByDefault);

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (!open) {
            searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
            setSearchParams(searchParams);
        }
    };

    const renderEnquiryModal = ({
        professional,
        branchOptions,
        onOpenChange,
    }: RenderEnquiryModalProps) => {
        const newBranchOptions =
            branchOptions || professional.branchOptions || [];

        if (!alwaysRenderModal && !isOpen) {
            return null;
        }

        return (
            <EnquiryModal
                professional={professional}
                branchOptions={newBranchOptions}
                open={isOpen}
                onOpenChange={(open) => {
                    handleOpenChange(open);
                    onOpenChange?.(open);
                }}
                drawer={mobile}
            />
        );
    };

    const openEnquiryModal = (changeUrl = false) => {
        setIsOpen(true);
        if (changeUrl) {
            searchParams.set(ENQUIRE_MODAL_SHOWUP_KEY, KEY_VALUE);
            setSearchParams(searchParams);
        }
    };

    const closeEnquiryModal = (changeUrl = false) => {
        setIsOpen(false);
        if (changeUrl && searchParams.has(ENQUIRE_MODAL_SHOWUP_KEY)) {
            searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
            setSearchParams(searchParams);
        }
    };

    useEffect(() => {
        // if not contains ENQUIRE_MODAL_TYPO_KEY and ENQUIRE_MODAL_SHOWUP_KEY,  do nothing
        if (
            !searchParams.has(ENQUIRE_MODAL_TYPO_KEY) &&
            !searchParams.has(ENQUIRE_MODAL_SHOWUP_KEY)
        ) {
            return;
        }
        // if ENQUIRE_MODAL_TYPO_KEY exits, replace it with correct key
        const enquiryTypo =
            searchParams.get(ENQUIRE_MODAL_TYPO_KEY) === KEY_VALUE;
        if (enquiryTypo) {
            searchParams.delete(ENQUIRE_MODAL_TYPO_KEY);
            searchParams.set(ENQUIRE_MODAL_SHOWUP_KEY, KEY_VALUE);
            setSearchParams(searchParams);
        }

        const showEnquiry =
            searchParams.get(ENQUIRE_MODAL_SHOWUP_KEY) === KEY_VALUE;
        setIsOpen(showEnquiry);
    }, [searchParams, setSearchParams]);

    return {
        renderEnquiryModal,
        openEnquiryModal,
        closeEnquiryModal,
        isEnquiryModalOpen: isOpen,
        isMobile: mobile,
        searchParams,
        setSearchParams,
    };
};
